import React, { Dispatch, SetStateAction } from 'react';
import { MapTool } from '../LayersToolbar/const';
import type { MapRef, ViewState } from 'react-map-gl/maplibre';
import { LngLatBoundsLike } from 'maplibre-gl';
import { TransitionInterpolator, TRANSITION_EVENTS } from '@deck.gl/core/typed';
import { Layer, Deck } from '@deck.gl/core/typed';
import { EditableGeoJsonLayer } from '@nebula.gl/layers';
import { ContextMenuItem } from '../MapMarker';
import { MapLayerConfigFragmentFragment } from '@kinetic-ui/data-layer';
import { Geometry } from '@turf/helpers';
export interface KineticLayer {
  type: 'radio' | 'checkbox';
  id: string;
  group: string;
  label: string;
  baseLayer?: boolean;
  onLayerClick?: (layerSelected: boolean, selectedLayers: string[]) => void;
}

export interface DeckTransitionProps {
  /**
   *  (Number, optional, default: 0) - Transition duration in milliseconds, default value 0, implies no transition.
   */
  transitionDuration?: number;
  /**
   * (Object, optional, default: LinearInterpolator) -
   * An interpolator object that defines the transition behavior between two viewports,
   *  deck.gl provides LinearInterpolator and FlyToInterpolator. Default value, LinearInterpolator,
   *  performs linear interpolation on ViewState fields. FlyToInterpolator animates ViewStates similar to
   * MapBox flyTo API and applicable for MapState, this is pretty useful when camera center changes by long distance.
   * But a user can provide any custom implementation for this object using TrasitionInterpolator base class.
   */
  transitionInterpolator?: TransitionInterpolator;
  /**
   * (Function, optional, default: t => t) - Easing function that can be used to achieve effects like "Ease-In-Cubic",
   * "Ease-Out-Cubic", etc. Default value performs Linear easing. (list of sample easing functions: http://easings.net/)
   */
  transitionEasing?: any;
  /**
   * transitionInterruption (TRANSITION_EVENTS (Number), optional, default: BREAK)
   * - This field controls how to process a new ViewState change that occurs while performing an existing transition.
   * This field has no impact once transition is complete. Here is the list of all possible values with resulting behavior.
   */
  transitionInterruption?: typeof TRANSITION_EVENTS;
}

export interface IKineticMapContext {
  fitBounds: (
    bounds: LngLatBoundsLike,
    bearing?: number,
    pitch?: number,
    transition?: DeckTransitionProps
  ) => void;
  setZoom: (zoom: number) => void;
  setDragEnabled: (drag: boolean) => void;
  mapRef: React.MutableRefObject<MapRef>;
  deckRef: React.MutableRefObject<Deck>;
  setViewState: Dispatch<SetStateAction<Partial<ViewState & DeckTransitionProps>>>;
  selectedBaseLayerConfig?: MapLayerConfigFragmentFragment;
}

export interface MapContextData {
  registerLayer: (layer: KineticLayer) => void;
  layers: KineticLayer[];
  isEntitySelected: (id: string) => boolean;
  isAnyEntitySelected: (id: string[]) => boolean;
  hasSelectedEntities: boolean;
  selectedEntities: Set<string>;
  setSelectedEntities: Dispatch<SetStateAction<Set<string>>>;
  selectedEntitiesCount: number;
  setEntities: (selectedEntities: string[]) => void;
  removeEntities: (selectedEntities: string[]) => void;
  removeAllEntities: () => void;
  setEntity: (selectedEntity: string) => void;
  selectedLayers: string[];
  setLayerSelected: (id: string, checked: boolean) => void;
  setSelectedLayers: (ids: string[]) => void;
  isEntitiesOpen: boolean;
  toggleEntities: () => void;
  openEntities: () => void;
  setClickableMarkers: Dispatch<SetStateAction<boolean>>;
  clickableMarkers: boolean;
  activeMapTool: MapTool;
  setActiveMapTool: Dispatch<SetStateAction<MapTool | null>>;
  clusteringEnabled: boolean;
  isStatusBarOpen: boolean;
  toggleStatusBar: () => void;
  activeStatusBar: string;
  setActiveStatusBar: Dispatch<SetStateAction<string>>;
  cursor: string;

  setExtraDeckGlLayers: Dispatch<SetStateAction<(Layer | EditableGeoJsonLayer)[]>>;
  hiddenMarkers: string[];
  setHiddenMarkers: Dispatch<React.SetStateAction<string[]>>;
  hasEntityDrawer: boolean;
  setAdditionalMapContextMenuItems: Dispatch<React.SetStateAction<ContextMenuItem[]>>;
  additionalMapContextMenuItems: ContextMenuItem[];
  intersectsWithExtraDeckGlData: (geometry: Geometry) => string[];
}

export const KineticMapContext = React.createContext<IKineticMapContext>({
  fitBounds: () => null,
  setZoom: () => null,
  mapRef: { current: null! },
  setViewState: () => null,
  setDragEnabled: () => null,
  deckRef: { current: null! },
});

export const MapContext = React.createContext<MapContextData>({
  registerLayer: () => null,
  layers: [],
  selectedLayers: [],
  isEntitySelected: () => false,
  isAnyEntitySelected: () => false,
  hasSelectedEntities: false,
  selectedEntities: new Set(),
  setSelectedEntities: () => null,
  selectedEntitiesCount: 0,
  setEntities: () => null,
  removeEntities: () => null,
  removeAllEntities: () => null,
  setEntity: () => null,
  setLayerSelected: () => null,
  setSelectedLayers: () => null,
  isEntitiesOpen: false,
  toggleEntities: () => null,
  openEntities: () => null,
  setClickableMarkers: () => true,
  clickableMarkers: false,
  activeMapTool: null!,
  setActiveMapTool: () => null,
  clusteringEnabled: true,
  isStatusBarOpen: false,
  toggleStatusBar: () => null,
  activeStatusBar: '',
  setActiveStatusBar: () => null,

  setExtraDeckGlLayers: () => null,
  cursor: null!,
  hiddenMarkers: [],
  setHiddenMarkers: () => null,
  hasEntityDrawer: false,
  setAdditionalMapContextMenuItems: () => null,
  additionalMapContextMenuItems: [],
  intersectsWithExtraDeckGlData: () => [],
});
